import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { UserRefSchema } from './user.js';

/**
 * Компоненты проекта (docs/13 §4).
 *
 * Компонент — подсистема внутри проекта: «Платежи», «Уведомления», «Отчёты».
 * Имя задаётся пользователем и сохраняет регистр; уникальность в проекте —
 * без учёта регистра (docs/13 §3).
 */

/** Имя компонента: непустое после trim, не длиннее 60 (varchar(60)). */
const ComponentNameSchema = z
  .string()
  .trim()
  .min(1, 'Введите название')
  .max(60, 'Не больше 60 символов');

const ComponentDescriptionSchema = z.string().max(2_000, 'Не больше 2000 символов');

export const ComponentSchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  name: z.string(),
  description: z.string(),
  /** Владелец компонента: к кому маршрутизируются задачи. */
  lead: UserRefSchema.nullable(),
  /** Исполнитель по умолчанию при создании задачи с этим компонентом. */
  defaultAssignee: UserRefSchema.nullable(),
  /** Мягкое удаление: архивный компонент скрыт из выбора, но живёт в истории. */
  archivedAt: IsoDateTimeSchema.nullable(),
  /** Число активных (не удалённых) задач компонента — для списка в настройках. */
  issueCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Component = z.infer<typeof ComponentSchema>;

export const ComponentListSchema = z.object({ items: z.array(ComponentSchema) });
export type ComponentList = z.infer<typeof ComponentListSchema>;

export const CreateComponentBodySchema = z
  .object({
    name: ComponentNameSchema,
    description: ComponentDescriptionSchema.default(''),
    leadId: UuidSchema.nullable().default(null),
    defaultAssigneeId: UuidSchema.nullable().default(null),
  })
  .strict();
export type CreateComponentBody = z.infer<typeof CreateComponentBodySchema>;

export const UpdateComponentBodySchema = z
  .object({
    name: ComponentNameSchema.optional(),
    description: ComponentDescriptionSchema.optional(),
    leadId: UuidSchema.nullable().optional(),
    defaultAssigneeId: UuidSchema.nullable().optional(),
  })
  .strict();
export type UpdateComponentBody = z.infer<typeof UpdateComponentBodySchema>;

/** Список компонентов проекта: по умолчанию только активные (docs/13 §4). */
export const ComponentListQuerySchema = z.object({
  includeArchived: z
    .enum(['1', 'true'])
    .optional()
    .transform((value) => value !== undefined),
});
export type ComponentListQuery = z.infer<typeof ComponentListQuerySchema>;

/** Ссылка на компонент в карточке задачи. */
export const IssueComponentRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
});
export type IssueComponentRef = z.infer<typeof IssueComponentRefSchema>;
